import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import Breadcrumb from '../components/Breadcrumb'
import { ArrowUpRight, ExternalLink } from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

interface PortfolioItem {
  label: string
  href: string
  image: string
  description: string
  category: string
}

const portfolioItems: PortfolioItem[] = [
  {
    label: 'Technoarreda | Interior Design',
    href: 'https://technoarreda-interior-design.vercel.app/',
    image: '/images/technoarreda.png',
    description: 'A modern interior design platform showcasing premium spaces with elegant visual storytelling.',
    category: 'Interior Design'
  },
  {
    label: 'Nymphai | Cosematics',
    href: 'https://nymphai-cosematics.vercel.app/',
    image: '/images/cosematics.png',
    description: 'Luxury cosmetics brand site with immersive product showcases and seamless shopping experience.',
    category: 'Cosmetics'
  },
  {
    label: 'Redsun | Business',
    href: 'https://redsun-business-website.vercel.app/',
    image: '/images/redsun.png',
    description: 'Corporate business website built for impact — clean, professional, and conversion-focused.',
    category: 'Business'
  },
  {
    label: 'Voxelo | E-Commerce Solution',
    href: 'https://voxelo-ecommerce-solution.vercel.app/',
    image: '/images/voxelo.png',
    description: 'Full-featured e-commerce platform with streamlined checkout and dynamic product management.',
    category: 'E-Commerce'
  },
  {
    label: 'Loud | Digital',
    href: 'https://loud-digital.vercel.app',
    image: '/images/loud.png',
    description: 'Digital agency site that amplifies brand presence through bold design and strategic content.',
    category: 'Digital'
  },
  {
    label: 'Music | history',
    href: 'https://music-history-three.vercel.app/',
    image: '/images/music.png',
    description: 'An interactive journey through music history with rich media and timeline exploration.',
    category: 'Music'
  },
  {
    label: 'Stokt | AI',
    href: 'https://stokt-cretive-design.vercel.app',
    image: '/images/stokt.png',
    description: 'AI-driven creative design platform that merges machine learning with artistic expression.',
    category: 'AI'
  },
  {
    label: 'Buck Sauce | Food',
    href: 'https://buck-sauce.vercel.app',
    image: '/images/buck.png',
    description: 'Bold food brand site showcasing signature sauces with mouth-watering visuals and storytelling.',
    category: 'Food'
  }
]

const categories = ['All', ...new Set(portfolioItems.map(p => p.category))]

const schema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Portfolio - Technifire Software",
  "description": "Explore our latest projects across interior design, cosmetics, e-commerce, AI, and more.",
  "url": "https://technifire.io/portfolio",
  "isPartOf": { "@type": "WebSite", "name": "Technifire Software", "url": "https://technifire.io" }
}

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState('All')
  const gridRef = useRef<HTMLDivElement>(null)

  const filteredItems = activeFilter === 'All'
    ? portfolioItems
    : portfolioItems.filter(p => p.category === activeFilter)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = gridRef.current?.querySelectorAll('.portfolio-card')
      if (cards && cards.length > 0) {
        gsap.fromTo(cards, { y: 80, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: gridRef.current, start: 'top 85%', toggleActions: 'play none none none', invalidateOnRefresh: true }
        })
      }
    }, gridRef)
    return () => ctx.revert()
  }, [activeFilter])

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      <Helmet>
        <title>Portfolio — Technifire Software</title>
        <meta name="description" content="Explore our latest projects across interior design, cosmetics, e-commerce, AI, and more." />
        <link rel="canonical" href="https://technifire.io/portfolio" />
        <meta property="og:title" content="Portfolio — Technifire Software" />
        <meta property="og:description" content="Explore our latest projects across interior design, cosmetics, e-commerce, AI, and more." />
        <meta property="og:url" content="https://technifire.io/portfolio" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <Breadcrumb current="PORTFOLIO" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-6">
          <div>
            <span className="text-mono text-[#9A9A9A] mb-2 block">PORTFOLIO</span>
            <h1 className="text-h2 uppercase text-white">
              OUR <span className="inline-block w-16 h-8 lg:w-24 lg:h-10 bg-white rounded mx-1 align-middle" /> WORK
            </h1>
          </div>
          <p className="text-[#9A9A9A] max-w-md">
            Handcrafted digital experiences we've built for brands around the world.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`filter-pill ${activeFilter === cat ? 'active' : ''}`}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Project Grid */}
      <div ref={gridRef} className="px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="portfolio-card card-hover rounded-2xl overflow-hidden bg-[#0a0a0a] group block"
            >
              <div className="overflow-hidden relative">
                <img
                  src={item.image}
                  alt={item.label}
                  className="w-full h-56 lg:h-64 object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-600/80 via-red-600/20 to-transparent to-60%" />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 right-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    <ExternalLink className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="absolute bottom-3 left-3">
                  <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm text-[10px] text-white/80 uppercase tracking-wider">
                    {item.category}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-white font-medium text-base leading-tight group-hover:text-[#B7000E] transition-colors duration-300">
                    {item.label}
                  </h3>
                  <ArrowUpRight className="w-4 h-4 text-[#9A9A9A] shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-1 group-hover:translate-x-0" />
                </div>
                <p className="text-sm text-[#666] mt-2 leading-relaxed line-clamp-2">
                  {item.description}
                </p>
                <div className="mt-4 pt-3 border-t border-white/5">
                  <span className="text-xs text-[#9A9A9A] group-hover:text-white transition-colors flex items-center gap-1.5">
                    Visit Project
                    <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#9A9A9A]">No projects found in this category yet.</p>
          </div>
        )}
      </div>
    </div>
  )
}
